import { useEffect, useState, useCallback } from "react";
import { Plus, Printer, Pencil, BadgeDollarSign, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, DataTable, StatusBadge, FormDialog, Field, Loading } from "@/components/bits";
import { MultiSelect, passes } from "@/components/MultiSelect";
import { PrintOverlay, SPKPrintView } from "@/components/printDocs";
import { PrintModeToggle, SpkHargaDialog } from "@/components/SpkHarga";
import { SpkMarkingEditor } from "@/components/SpkMarkingEditor";
import { SpkMarkingPicker } from "@/components/SpkMarkingPicker";
import { AreaPicker } from "@/components/AreaPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtTon } from "@/lib/utils";

const EMPTY = { no: "", tanggal: new Date().toISOString().slice(0, 10), projectId: "", vendorId: "", area: "", qty: "", totalTon: "", profile: "WF350×175×7×11", status: "Aktif", rateTon: "", tanggalSelesai: "" };

export default function SPK() {
  const [rows, setRows] = useState(null);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [fProyek, setFProyek] = useState([]);
  const [fVendor, setFVendor] = useState([]);
  const [fStatus, setFStatus] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [printSpk, setPrintSpk] = useState(null);
  const [editSpk, setEditSpk] = useState(null);
  const [editForm, setEditForm] = useState({});
  const openEdit = (r) => { setEditSpk(r); setEditForm({ jenisPekerjaan: r.jenisPekerjaan || "FABRIKASI", menggantikanSpk: r.menggantikanSpk || "", rateTon: r.rateTon || "", tanggalSelesai: r.tanggalSelesai || "", area: r.area || "", status: r.status || "Aktif" }); };
  const saveEdit = async () => {
    try {
      const { data } = await api.patch(`/spk/${editSpk.id}`, { ...editForm, rateTon: Number(editForm.rateTon) || 0 });
      if (data.replacement?.found) toast.success(`SPK ${data.replacement.oldNo} ditandai Digantikan · ${data.replacement.moved} marking dipindah ke ${data.no}`);
      else if (data.replacement && data.replacement.found === false) toast.warning(`SPK ${data.replacement.oldNo} tidak ditemukan — info disimpan tanpa memindahkan marking`);
      else toast.success("Info SPK diperbarui");
      setEditSpk(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const [printMarks, setPrintMarks] = useState([]);
  const [commercial, setCommercial] = useState(false);
  const [hargaSpk, setHargaSpk] = useState(null);
  const { user } = useApp();

  const hapusSpk = async (r) => {
    if (!window.confirm(`Hapus SPK ${r.no} beserta marking-nya?\nTindakan ini tidak dapat dibatalkan.`)) return;
    try {
      const { data } = await api.delete(`/spk/${r.id}`);
      toast.success(`SPK ${r.no} dihapus (${data.deletedMarkings} marking) / 工单已删除`);
      load();
    } catch (e) {
      if (e?.response?.status === 400 && /progres|Hapus Paksa/i.test(fmtErr(e))) {
        if (!window.confirm(`${fmtErr(e)}\n\nHAPUS PAKSA ${r.no}?\nSemua QC, STB, laporan STB, delivery & riwayat label marking SPK ini ikut terhapus permanen.`)) return;
        try {
          const { data } = await api.delete(`/spk/${r.id}?force=true`);
          toast.success(`SPK ${r.no} dihapus paksa (${data.deletedMarkings} marking, ${data.stbs ?? 0} STB, ${data.scans ?? 0} scan, ${data.deliveries ?? 0} delivery)`);
          load();
        } catch (e2) { toast.error(fmtErr(e2)); }
        return;
      }
      toast.error(fmtErr(e));
    }
  };

  const cetakSpk = async (r) => {
    try {
      const { data } = await api.get(`/markings?spkId=${r.id}&limit=1000`);
      setPrintMarks(data);
      setPrintSpk(r);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const load = useCallback(() => {
    api.get("/spk").then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);
  const shown = (rows || []).filter((r) => passes(fProyek, r.projectId) && passes(fVendor, r.vendorId) && passes(fStatus, r.status));
  const filterActive = fProyek.length || fVendor.length || fStatus.length;

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  }, []);

  const [pick, setPick] = useState({ sel: [], qtys: {} });
  const [mode, setMode] = useState("dl");   // dl = pilih marking dari delivery list · manual = batch otomatis
  const submit = async () => {
    try {
      if (mode === "dl") {
        if (!form.projectId || !form.vendorId) { toast.error("Pilih proyek dan vendor"); return; }
        if (!pick.sel.length) { toast.error("Centang minimal 1 marking yang akan di-SPK-kan"); return; }
        const qtys = Object.fromEntries(Object.entries(pick.qtys).filter(([id, q]) => pick.sel.includes(id) && q !== "" && q != null).map(([id, q]) => [id, Number(q)]));
        const hargas = Object.fromEntries(Object.entries(pick.hargas || {}).filter(([id]) => pick.sel.includes(id)).map(([id, h]) => [id, { unitHarga: h.unit || "TON", hargaSatuan: h.harga === "" || h.harga == null ? Number(form.rateTon || 0) : Number(h.harga) }]));
        const { data } = await api.post(`/projects/${form.projectId}/delivery-list/create-spk`, { vendorId: form.vendorId, tanggal: form.tanggal, tanggalSelesai: form.tanggalSelesai || "", rateTon: Number(form.rateTon || 0), no: form.no || "", area: form.area || "", itemIds: pick.sel, qtys, hargas });
        if (data.skipped?.length) toast.warning(data.skipped.join(" · "));
        toast.success(`SPK ${data.spk.no} dibuat — ${data.created} marking · ${data.spk.qty} pcs · ${fmtTon(data.spk.totalTon)} Ton / 派工单已创建`);
      } else {
        await api.post("/spk", { ...form, qty: Number(form.qty), totalTon: Number(form.totalTon), rateTon: Number(form.rateTon || 0), tanggalSelesai: form.tanggalSelesai || "", no: form.no || "" });
        toast.success("SPK dibuat — marking otomatis ter-generate / 派工单已创建");
      }
      setOpen(false); setForm(EMPTY); setPick({ sel: [], qtys: {} }); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5">
      <PageHeader id="SPK / 工作指令" zh="外包派工单" desc="Surat perintah kerja subkon — marking barcode dibuat otomatis."
        actions={<Button className="bg-sky-600 hover:bg-sky-500" onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="spk-add-button"><Plus className="mr-1.5 h-4 w-4" />Tambah SPK</Button>} />

      <div className="panel p-4">
        <div className="mb-3 flex flex-wrap gap-2" data-testid="spk-filters">
          <MultiSelect testid="spk-filter-project" placeholder="Semua Proyek" width="w-48" menuWidth="w-80" options={projects.map((p) => ({ value: p.id, label: p.kode, sub: p.nama }))} value={fProyek} onChange={setFProyek} />
          <MultiSelect testid="spk-filter-vendor" placeholder="Semua Vendor" width="w-44" options={vendors.map((v) => ({ value: v.id, label: v.singkatan || v.nama, sub: v.singkatan ? v.nama : undefined }))} value={fVendor} onChange={setFVendor} />
          <MultiSelect testid="spk-filter-status" placeholder="Semua Status" width="w-40" menuWidth="w-56" options={["Aktif", "Draft", "Selesai", "Digantikan"].map((s) => ({ value: s, label: s }))} value={fStatus} onChange={setFStatus} />
          {filterActive ? <button type="button" onClick={() => { setFProyek([]); setFVendor([]); setFStatus([]); }} title="Reset filter" className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200" data-testid="spk-filter-reset"><X className="h-3.5 w-3.5" /></button> : null}
          <span className="ml-auto self-center font-mono text-xs text-slate-500" data-testid="spk-count">{rows ? `${shown.length} / ${rows.length} SPK` : ""}</span>
        </div>

        {!rows ? <Loading /> : (
          <DataTable testid="spk-table" rows={shown} columns={[
            { key: "no", label: "No", render: (r, i) => <span className="font-mono text-slate-500">{i + 1}</span> },
            { key: "no2", label: "No SPK", render: (r) => <span className="font-mono text-sky-400">{r.no}</span> },
            { key: "tanggal", label: "Tanggal", render: (r) => <span className="font-mono text-slate-400 text-xs">{r.tanggal}</span> },
            { key: "projectName", label: "Proyek", render: (r) => r.projectName },
            { key: "vendorName", label: "Vendor", render: (r) => <span className="inline-flex items-center gap-1.5">{r.vendorName}{r.vendorSingkatan && <span className="rounded border border-indigo-500/30 bg-indigo-500/10 px-1.5 py-0.5 font-mono text-[10px] text-indigo-300">{r.vendorSingkatan}</span>}</span> },
            { key: "area", label: "Area", render: (r) => <span className="rounded border border-slate-700 px-2 py-0.5 font-mono text-xs">{r.area}</span> },
            { key: "qty", label: "Qty (pcs)", render: (r) => <span className="font-mono">{r.qty}</span> },
            { key: "totalTon", label: "Total Ton", render: (r) => <span className="font-mono">{fmtTon(r.totalTon)}</span> },
            { key: "status", label: "Status", render: (r) => (
              <span className="inline-flex flex-col gap-0.5">
                <StatusBadge status={r.status} />
                {r.digantikanOleh && <span className="font-mono text-[9px] text-slate-500">→ {r.digantikanOleh}</span>}
                {r.menggantikanSpk && <span className="font-mono text-[9px] text-violet-300">revisi {r.menggantikanSpk}</span>}
              </span>
            )},
            { key: "aksi", label: "Aksi", render: (r) => (
              <span className="flex gap-1.5">
                <Button size="sm" variant="outline" className="h-7 border-slate-700 text-xs" onClick={() => cetakSpk(r)} data-testid={`spk-print-${r.no}`}>
                  <Printer className="mr-1 h-3 w-3" />Cetak
                </Button>
                {user?.role === "admin" && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openEdit(r)} title="Edit SPK & marking" data-testid={`spk-edit-${r.no}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
                {user?.role === "admin" && (
                  <Button size="sm" variant="outline" className="h-7 border-amber-500/40 text-xs text-amber-300 hover:bg-amber-500/10" onClick={() => setHargaSpk(r)} title="Edit harga per marking (TON / METER)" data-testid={`spk-harga-${r.no}`}>
                    <BadgeDollarSign className="mr-1 h-3.5 w-3.5" />Harga
                  </Button>
                )}
                {user?.role === "admin" && (
                  <Button size="sm" variant="outline" className="h-7 border-rose-500/40 text-xs text-rose-300 hover:bg-rose-500/10" onClick={() => hapusSpk(r)} title="Hapus SPK — nomor SPK dihapus, marking kembali Belum SPK (Hapus Paksa bila sudah ada progres)" data-testid={`spk-delete-${r.no}`}>
                    <Trash2 className="mr-1 h-3.5 w-3.5" />Hapus
                  </Button>
                )}
              </span>
            )},
          ]} />
        )}
      </div>

      <FormDialog open={!!editSpk} onOpenChange={(v) => !v && setEditSpk(null)} testid="spk-edit-dialog" title={`Edit SPK · ${editSpk?.no || ""}`} onSubmit={saveEdit} submitLabel="Simpan Info SPK" wide>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Jenis Pekerjaan / 工种"><Input value={editForm.jenisPekerjaan || ""} onChange={(e) => setEditForm({ ...editForm, jenisPekerjaan: e.target.value.toUpperCase() })} data-testid="spk-edit-jenis" /></Field>
          <Field label="Harga per Ton (Rp)"><Input type="number" min="0" value={editForm.rateTon} onChange={(e) => setEditForm({ ...editForm, rateTon: e.target.value })} data-testid="spk-edit-rate" /></Field>
        </div>
        <Field label="Menggantikan SPK (revisi) — SPK lama otomatis ditandai Digantikan & markingnya dipindah ke SPK ini">
          <Input value={editForm.menggantikanSpk || ""} onChange={(e) => setEditForm({ ...editForm, menggantikanSpk: e.target.value.toUpperCase() })} placeholder="mis. FAB-PJ-2026-001-09-001" className="font-mono" data-testid="spk-edit-menggantikan" />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Target Selesai"><Input type="date" value={editForm.tanggalSelesai || ""} onChange={(e) => setEditForm({ ...editForm, tanggalSelesai: e.target.value })} data-testid="spk-edit-selesai" /></Field>
          <Field label="Area">
            <AreaPicker testid="spk-edit-area" value={editForm.area} onChange={(v) => setEditForm({ ...editForm, area: v })} extra={[...vendors.map((v) => v.area), ...(rows || []).map((r) => r.area)]} />
          </Field>
          <Field label="Status">
            <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
              <SelectTrigger className="border-slate-800 bg-slate-900/70" data-testid="spk-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0D1524] border-slate-800">{["Aktif", "Draft", "Selesai", "Digantikan"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <div className="border-t border-slate-800 pt-3">
          <SpkMarkingEditor spk={editSpk} isAdmin={user?.role === "admin"} onChanged={() => load()} />
        </div>
      </FormDialog>

      <FormDialog open={open} onOpenChange={setOpen} testid="spk-dialog" title="Tambah SPK / 新建派工单" onSubmit={submit} submitLabel={mode === "dl" ? `Buat SPK (${pick.sel.length} marking)` : "Buat SPK + Marking"} wide>
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="spk-mode-toggle">
          <span className="text-slate-400">Sumber marking:</span>
          {[["dl", "Pilih marking dari Delivery List + qty"], ["manual", "Manual (batch otomatis @50 pcs)"]].map(([k, t]) => (
            <button type="button" key={k} onClick={() => setMode(k)} className={`rounded border px-2.5 py-1 ${mode === k ? "border-sky-500/60 bg-sky-500/10 text-sky-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`} data-testid={`spk-mode-${k}`}>{t}</button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Tanggal"><Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} data-testid="spk-tanggal-input" /></Field>
          <Field label="Proyek">
            <Select value={form.projectId} onValueChange={(v) => { setForm({ ...form, projectId: v }); setPick({ sel: [], qtys: {} }); }} data-testid="spk-project-select">
              <SelectTrigger className="border-slate-800 bg-slate-900/70"><SelectValue placeholder="Pilih Proyek" /></SelectTrigger>
              <SelectContent className="bg-[#0D1524] border-slate-800 max-h-56">
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.kode} · {p.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Vendor">
            <Select value={form.vendorId} onValueChange={(v) => {
              const vend = vendors.find((x) => x.id === v);
              setForm({ ...form, vendorId: v, area: form.area || vend?.area || "" });
            }} data-testid="spk-vendor-select">
              <SelectTrigger className="border-slate-800 bg-slate-900/70"><SelectValue placeholder="Pilih Vendor" /></SelectTrigger>
              <SelectContent className="bg-[#0D1524] border-slate-800 max-h-56">
                {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.singkatan ? `${v.singkatan} · ` : ""}{v.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        {mode === "dl" && (
          <Field label="Marking yang di-SPK-kan — centang marking, isi qty (default = seluruh sisa belum SPK) & harga satuan per marking (default = Harga per Ton SPK)">
            <SpkMarkingPicker projectId={form.projectId} value={pick} onChange={setPick} defaultHarga={form.rateTon} />
          </Field>
        )}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Area"><AreaPicker testid="spk-area" value={form.area} onChange={(v) => setForm({ ...form, area: v })} extra={[...vendors.map((v) => v.area), ...(rows || []).map((r) => r.area)]} /></Field>
          {mode === "manual" ? <>
            <Field label="Qty (pcs)"><Input required type="number" min="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} placeholder="120" data-testid="spk-qty-input" /></Field>
            <Field label="Total Ton"><Input required type="number" min="1" step="0.1" value={form.totalTon} onChange={(e) => setForm({ ...form, totalTon: e.target.value })} placeholder="120" data-testid="spk-ton-input" /></Field>
          </> : <>
            <Field label="Harga per Ton (Rp, opsional)"><Input type="number" min="0" value={form.rateTon} onChange={(e) => setForm({ ...form, rateTon: e.target.value })} placeholder="700000" data-testid="spk-rate-input" /></Field>
            <Field label="Target Selesai (opsional)"><Input type="date" value={form.tanggalSelesai} onChange={(e) => setForm({ ...form, tanggalSelesai: e.target.value })} data-testid="spk-target-selesai-input" /></Field>
          </>}
        </div>
        {mode === "manual" && <>
          <Field label="Profil Beam"><Input value={form.profile} onChange={(e) => setForm({ ...form, profile: e.target.value })} data-testid="spk-profile-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Harga per Ton (Rp, opsional)"><Input type="number" min="0" value={form.rateTon} onChange={(e) => setForm({ ...form, rateTon: e.target.value })} placeholder="700000" data-testid="spk-rate-input" /></Field>
            <Field label="Target Selesai (opsional)"><Input type="date" value={form.tanggalSelesai} onChange={(e) => setForm({ ...form, tanggalSelesai: e.target.value })} data-testid="spk-target-selesai-input" /></Field>
          </div>
        </>}
        <Field label="No SPK — kosongkan untuk otomatis"><Input value={form.no} onChange={(e) => setForm({ ...form, no: e.target.value.toUpperCase() })} placeholder="otomatis" className="font-mono" data-testid="spk-no-input" /></Field>
        <p className="text-[11px] text-slate-500">No SPK otomatis: <b className="font-mono text-slate-400">FAB-&lt;kode proyek&gt;-&lt;bulan&gt;-&lt;no urut&gt;</b> (mis. FAB-PJ-2026-001-10-001). {mode === "dl" ? "Tonase SPK = Σ qty × berat/pcs dari delivery list." : "Marking barcode (batch @50 pcs) dibuat otomatis."}</p>
      </FormDialog>

      {hargaSpk && <SpkHargaDialog spk={hargaSpk} onClose={() => setHargaSpk(null)} onSaved={load} />}

      {printSpk && (
        <PrintOverlay testid="spk-print-overlay" onClose={() => setPrintSpk(null)} toolbar={<PrintModeToggle commercial={commercial} onChange={setCommercial} />}>
          <SPKPrintView spk={printSpk} markings={printMarks} user={user} commercial={commercial} />
        </PrintOverlay>
      )}
    </div>
  );
}